import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Filter, Plus, MoreVertical, Building2, AlertCircle, Loader2, ExternalLink, Mail, X, Send, CheckCircle } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

interface SetupProgress {
  hasStudioName: boolean;
  hasServices: boolean;
  hasTeamMembers: boolean;
  hasRooms: boolean;
  isComplete: boolean;
  completedSteps: number;
  counts: {
    services: number;
    therapists: number;
    rooms: number;
    clients: number;
  };
}

interface Studio {
  id: string;
  userId?: string; // Added for impersonation
  name: string;
  owner: string;
  email: string;
  plan: 'trial' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'inactive' | 'suspended';
  createdAt: string;
  bookingsCount: number;
  setupProgress?: SetupProgress;
}

const planColors = {
  trial: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
  starter: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  professional: 'bg-sage/10 text-sage',
  enterprise: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
};

const statusColors = {
  active: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  inactive: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
  suspended: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
};

// Onboarding progress indicator component
function OnboardingProgress({ progress }: { progress?: SetupProgress }) {
  if (!progress) {
    return <span className="text-stone-400 text-sm">-</span>;
  }

  const { completedSteps, isComplete, hasStudioName, hasServices, hasTeamMembers, hasRooms, counts } = progress;

  // Color based on completion
  const bgColor = isComplete
    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
    : completedSteps >= 2
    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';

  // Tooltip content
  const tooltipLines = [
    `${hasStudioName ? '✓' : '✗'} Studio Name`,
    `${hasServices ? '✓' : '✗'} Services (${counts.services})`,
    `${hasTeamMembers ? '✓' : '✗'} Team (${counts.therapists})`,
    `${hasRooms ? '✓' : '✗'} Rooms (${counts.rooms})`,
  ];

  return (
    <div className="group relative inline-block">
      <span className={`text-xs font-medium px-2 py-1 rounded-full ${bgColor}`}>
        {completedSteps}/4
      </span>
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-20">
        <div className="bg-stone-800 dark:bg-stone-700 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
          <div className="font-medium mb-1">Setup Progress</div>
          {tooltipLines.map((line, i) => (
            <div key={i} className={line.startsWith('✓') ? 'text-green-400' : 'text-red-400'}>
              {line}
            </div>
          ))}
          {counts.clients > 0 && (
            <div className="text-stone-400 mt-1 pt-1 border-t border-stone-600">
              {counts.clients} client{counts.clients !== 1 ? 's' : ''}
            </div>
          )}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-stone-800 dark:border-t-stone-700" />
        </div>
      </div>
    </div>
  );
}

export default function StudiosPage() {
  const { getToken } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [studios, setStudios] = useState<Studio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);

  // Bulk email modal state
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [selectedStudios, setSelectedStudios] = useState<string[]>([]);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailMessage, setBulkEmailMessage] = useState('');
  const [sendingBulkEmail, setSendingBulkEmail] = useState(false);
  const [bulkEmailResult, setBulkEmailResult] = useState<{ success: boolean; message: string } | null>(null);

  // Add Studio modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addStudioForm, setAddStudioForm] = useState({
    name: '',
    ownerEmail: '',
    plan: 'trial' as 'trial' | 'starter' | 'professional' | 'enterprise',
  });
  const [addingStudio, setAddingStudio] = useState(false);
  const [addStudioResult, setAddStudioResult] = useState<{ success: boolean; message: string } | null>(null);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStudios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/studios`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch studios');
      }
      const data = await response.json();
      if (data.studios) {
        setStudios(data.studios);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load studios');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchStudios();
  }, [fetchStudios]);

  const filteredStudios = studios.filter(
    (studio) =>
      studio.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      studio.owner.toLowerCase().includes(searchQuery.toLowerCase()) ||
      studio.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle viewing studio dashboard (impersonation)
  const handleViewDashboard = async (studio: Studio) => {
    if (!studio.userId) {
      setError('Cannot view dashboard: Studio user ID not available');
      return;
    }

    setImpersonating(studio.id);
    setOpenActionMenu(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/studios/${studio.userId}/impersonate`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate impersonation link');
      }

      const data = await response.json();
      if (data.dashboardUrl) {
        // Open the dashboard in a new tab
        window.open(data.dashboardUrl, '_blank');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to view dashboard');
    } finally {
      setImpersonating(null);
    }
  };

  // Handle bulk email
  const handleSendBulkEmail = async () => {
    if (selectedStudios.length === 0 || !bulkEmailSubject || !bulkEmailMessage) {
      return;
    }

    setSendingBulkEmail(true);
    setBulkEmailResult(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();

      // Get userIds for selected studios
      const userIds = studios
        .filter(s => selectedStudios.includes(s.id) && s.userId)
        .map(s => s.userId!);

      const response = await fetch(`${apiUrl}/admin/bulk/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userIds,
          subject: bulkEmailSubject,
          message: bulkEmailMessage,
        }),
      });

      const data = await response.json();
      setBulkEmailResult({
        success: response.ok,
        message: data.message || (response.ok ? 'Emails sent successfully' : 'Failed to send emails'),
      });

      if (response.ok) {
        // Clear form after 3 seconds
        setTimeout(() => {
          setShowBulkEmailModal(false);
          setBulkEmailSubject('');
          setBulkEmailMessage('');
          setSelectedStudios([]);
          setBulkEmailResult(null);
        }, 3000);
      }
    } catch (err) {
      setBulkEmailResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to send emails',
      });
    } finally {
      setSendingBulkEmail(false);
    }
  };

  // Toggle studio selection for bulk operations
  const toggleStudioSelection = (studioId: string) => {
    setSelectedStudios(prev =>
      prev.includes(studioId)
        ? prev.filter(id => id !== studioId)
        : [...prev, studioId]
    );
  };

  // Select all visible studios
  const selectAllStudios = () => {
    const allIds = filteredStudios.filter(s => s.userId).map(s => s.id);
    setSelectedStudios(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedStudios([]);
  };

  // Handle add studio
  const handleAddStudio = async () => {
    if (!addStudioForm.name || !addStudioForm.ownerEmail) {
      return;
    }

    setAddingStudio(true);
    setAddStudioResult(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();

      const response = await fetch(`${apiUrl}/admin/studios`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: addStudioForm.name,
          ownerEmail: addStudioForm.ownerEmail,
          plan: addStudioForm.plan,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setAddStudioResult({
          success: true,
          message: data.message || 'Studio created successfully! An invitation email has been sent to the owner.',
        });
        // Refresh studios list
        fetchStudios();
        // Reset form after delay
        setTimeout(() => {
          setShowAddModal(false);
          setAddStudioForm({ name: '', ownerEmail: '', plan: 'trial' });
          setAddStudioResult(null);
        }, 3000);
      } else {
        setAddStudioResult({
          success: false,
          message: data.error || 'Failed to create studio',
        });
      }
    } catch (err) {
      setAddStudioResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to create studio',
      });
    } finally {
      setAddingStudio(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">Error Loading Data</p>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-sage animate-spin" />
            <span className="text-stone-700 dark:text-stone-300">Loading studios...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800 dark:text-stone-100">Studios</h1>
          <p className="text-sm sm:text-base text-stone-500 dark:text-stone-400 mt-1">
            Manage all registered studios
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span className="sm:inline">Add Studio</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <input
            type="text"
            placeholder="Search studios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
          />
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors">
          <Filter className="w-5 h-5" />
          Filters
        </button>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedStudios.length > 0 && (
        <div className="bg-sage/10 border border-sage/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sage font-medium">
              {selectedStudios.length} studio{selectedStudios.length > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={clearSelection}
              className="text-sm text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkEmailModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-sage text-white rounded-lg font-medium hover:bg-sage/90 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Send Email
            </button>
          </div>
        </div>
      )}

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {filteredStudios.map((studio) => (
          <div key={studio.id} className={`bg-white dark:bg-stone-900 rounded-xl border p-4 ${selectedStudios.includes(studio.id) ? 'border-sage' : 'border-stone-200 dark:border-stone-800'}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {studio.userId && (
                  <input
                    type="checkbox"
                    checked={selectedStudios.includes(studio.id)}
                    onChange={() => toggleStudioSelection(studio.id)}
                    className="w-4 h-4 rounded border-stone-300 text-sage focus:ring-sage"
                  />
                )}
                <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-sage" />
                </div>
                <div>
                  <p className="font-medium text-stone-700 dark:text-stone-300">{studio.name}</p>
                  <p className="text-sm text-stone-500">{studio.owner}</p>
                </div>
              </div>
              <div className="relative" ref={openActionMenu === studio.id ? actionMenuRef : null}>
                <button
                  onClick={() => setOpenActionMenu(openActionMenu === studio.id ? null : studio.id)}
                  className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 transition-colors"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                {openActionMenu === studio.id && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-10">
                    {studio.userId && (
                      <button
                        onClick={() => handleViewDashboard(studio)}
                        disabled={impersonating === studio.id}
                        className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700"
                      >
                        {impersonating === studio.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ExternalLink className="w-4 h-4" />
                        )}
                        View Dashboard
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${planColors[studio.plan]}`}>
                {studio.plan}
              </span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${statusColors[studio.status]}`}>
                {studio.status}
              </span>
              <OnboardingProgress progress={studio.setupProgress} />
            </div>
            <div className="flex items-center justify-between text-sm text-stone-500">
              <span>{studio.bookingsCount.toLocaleString()} bookings</span>
              <span>Created {new Date(studio.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 overflow-visible">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200 dark:border-stone-800">
              <th className="w-12 px-4 py-4">
                <input
                  type="checkbox"
                  checked={selectedStudios.length === filteredStudios.filter(s => s.userId).length && selectedStudios.length > 0}
                  onChange={() => selectedStudios.length > 0 ? clearSelection() : selectAllStudios()}
                  className="w-4 h-4 rounded border-stone-300 text-sage focus:ring-sage"
                />
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Studio</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Owner</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Plan</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Status</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Onboarding</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Bookings</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Created</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400"></th>
            </tr>
          </thead>
          <tbody>
            {filteredStudios.map((studio) => (
              <tr key={studio.id} className={`border-b border-stone-100 dark:border-stone-800 last:border-0 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors ${selectedStudios.includes(studio.id) ? 'bg-sage/5' : ''}`}>
                <td className="px-4 py-4">
                  {studio.userId && (
                    <input
                      type="checkbox"
                      checked={selectedStudios.includes(studio.id)}
                      onChange={() => toggleStudioSelection(studio.id)}
                      className="w-4 h-4 rounded border-stone-300 text-sage focus:ring-sage"
                    />
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-sage" />
                    </div>
                    <span className="font-medium text-stone-700 dark:text-stone-300">{studio.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="text-stone-700 dark:text-stone-300">{studio.owner}</p>
                    <p className="text-sm text-stone-500">{studio.email}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${planColors[studio.plan]}`}>
                    {studio.plan}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${statusColors[studio.status]}`}>
                    {studio.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <OnboardingProgress progress={studio.setupProgress} />
                </td>
                <td className="px-6 py-4 text-stone-700 dark:text-stone-300">
                  {studio.bookingsCount.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-stone-500">
                  {new Date(studio.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <div className="relative" ref={openActionMenu === studio.id ? actionMenuRef : null}>
                    <button
                      onClick={() => setOpenActionMenu(openActionMenu === studio.id ? null : studio.id)}
                      className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 transition-colors"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    {openActionMenu === studio.id && (
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-10">
                        {studio.userId && (
                          <button
                            onClick={() => handleViewDashboard(studio)}
                            disabled={impersonating === studio.id}
                            className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700"
                          >
                            {impersonating === studio.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4" />
                            )}
                            View Dashboard
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500">
          Showing {filteredStudios.length} of {studios.length} studios
        </p>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors disabled:opacity-50" disabled>
            Previous
          </button>
          <button className="px-3 py-1 rounded-lg bg-sage text-white">1</button>
          <button className="px-3 py-1 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors">
            Next
          </button>
        </div>
      </div>

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-stone-800">
              <div>
                <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Send Bulk Email</h2>
                <p className="text-sm text-stone-500 mt-1">
                  Sending to {selectedStudios.length} studio{selectedStudios.length > 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowBulkEmailModal(false);
                  setBulkEmailResult(null);
                }}
                className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {bulkEmailResult ? (
                <div className={`p-4 rounded-xl flex items-center gap-3 ${bulkEmailResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                  {bulkEmailResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <p className={bulkEmailResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                    {bulkEmailResult.message}
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      value={bulkEmailSubject}
                      onChange={(e) => setBulkEmailSubject(e.target.value)}
                      placeholder="Email subject..."
                      className="w-full px-4 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                      Message
                    </label>
                    <textarea
                      value={bulkEmailMessage}
                      onChange={(e) => setBulkEmailMessage(e.target.value)}
                      placeholder="Write your message..."
                      rows={6}
                      className="w-full px-4 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage resize-none"
                    />
                  </div>

                  <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4">
                    <p className="text-sm text-stone-600 dark:text-stone-400">
                      <strong>Recipients:</strong>
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedStudios.slice(0, 5).map(id => {
                        const studio = studios.find(s => s.id === id);
                        return studio ? (
                          <span key={id} className="text-xs bg-sage/10 text-sage px-2 py-1 rounded-full">
                            {studio.name}
                          </span>
                        ) : null;
                      })}
                      {selectedStudios.length > 5 && (
                        <span className="text-xs text-stone-500">
                          +{selectedStudios.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {!bulkEmailResult && (
              <div className="flex items-center justify-end gap-3 p-6 border-t border-stone-200 dark:border-stone-800">
                <button
                  onClick={() => setShowBulkEmailModal(false)}
                  className="px-4 py-2 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendBulkEmail}
                  disabled={sendingBulkEmail || !bulkEmailSubject || !bulkEmailMessage}
                  className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingBulkEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Studio Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-stone-800">
              <div>
                <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-100">Add New Studio</h2>
                <p className="text-sm text-stone-500 mt-1">
                  Create a studio and invite the owner
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddStudioResult(null);
                  setAddStudioForm({ name: '', ownerEmail: '', plan: 'trial' });
                }}
                className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {addStudioResult ? (
                <div className={`p-4 rounded-xl flex items-center gap-3 ${addStudioResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                  {addStudioResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                  <p className={addStudioResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                    {addStudioResult.message}
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                      Studio Name *
                    </label>
                    <input
                      type="text"
                      value={addStudioForm.name}
                      onChange={(e) => setAddStudioForm({ ...addStudioForm, name: e.target.value })}
                      placeholder="e.g., Serenity Massage Studio"
                      className="w-full px-4 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                      Owner Email *
                    </label>
                    <input
                      type="email"
                      value={addStudioForm.ownerEmail}
                      onChange={(e) => setAddStudioForm({ ...addStudioForm, ownerEmail: e.target.value })}
                      placeholder="owner@example.com"
                      className="w-full px-4 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                    />
                    <p className="text-xs text-stone-500 mt-1">
                      An invitation will be sent to this email address
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                      Plan
                    </label>
                    <select
                      value={addStudioForm.plan}
                      onChange={(e) => setAddStudioForm({ ...addStudioForm, plan: e.target.value as any })}
                      className="w-full px-4 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                    >
                      <option value="trial">Trial (14 days free)</option>
                      <option value="starter">Starter</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            {!addStudioResult && (
              <div className="flex items-center justify-end gap-3 p-6 border-t border-stone-200 dark:border-stone-800">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setAddStudioForm({ name: '', ownerEmail: '', plan: 'trial' });
                  }}
                  className="px-4 py-2 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddStudio}
                  disabled={addingStudio || !addStudioForm.name || !addStudioForm.ownerEmail}
                  className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingStudio ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Studio
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
